import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

const PREVIEW_SIZE = 300;
const loadCanvasUtils = () => import('../CanvasUtils');

const QrCanvas = forwardRef(function QrCanvas(
  {
    targetUrl,
    foregroundColor,
    backgroundColor,
    logoFileOrUrl,
    logoScale,
    dotStyle,
    cornerSquareStyle,
    cornerDotStyle,
    preset,
  },
  ref
) {
  const containerRef = useRef(null);
  const fallbackCanvasRef = useRef(null);
  const styledInstanceRef = useRef(null);
  const renderPromiseRef = useRef(null);
  const fallbackReadyRef = useRef(false);
  const renderedLogoRef = useRef(false);
  const [isFallbackMode, setIsFallbackMode] = useState(false);
  const [isLogoOmitted, setIsLogoOmitted] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      canvas: fallbackCanvasRef.current,
      get hasLogo() {
        return renderedLogoRef.current;
      },
      async getBlob(extension = 'png') {
        await renderPromiseRef.current;
        if (styledInstanceRef.current?.getRawData) {
          try {
            const blob = await styledInstanceRef.current.getRawData(extension);
            if (blob) return blob;
          } catch (err) {
            console.warn(`Styled ${extension.toUpperCase()} export failed, using canvas fallback.`, err);
          }
        }

        if (extension !== 'png' || !targetUrl) return null;
        let canvas = fallbackCanvasRef.current;
        if (!fallbackReadyRef.current) {
          const { renderQrToCanvas } = await loadCanvasUtils();
          canvas = document.createElement('canvas');
          await renderQrToCanvas({
            canvas,
            text: targetUrl,
            foregroundColor,
            backgroundColor,
            logoFile: renderedLogoRef.current ? logoFileOrUrl : null,
            logoScale,
            logoPaddingPx: 1,
          });
        }
        if (!canvas?.toBlob) return null;
        return new Promise((resolve) => {
          canvas.toBlob((blob) => resolve(blob), 'image/png');
        });
      },
    }),
    [backgroundColor, foregroundColor, logoFileOrUrl, logoScale, targetUrl]
  );

  useEffect(() => {
    let active = true;
    const container = containerRef.current;
    const canvas = fallbackCanvasRef.current;

    async function renderPreview() {
      if (!container || !canvas) return;

      styledInstanceRef.current = null;
      fallbackReadyRef.current = false;
      renderedLogoRef.current = false;
      container.replaceChildren();
      setIsFallbackMode(false);
      setIsLogoOmitted(false);

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width || PREVIEW_SIZE, canvas.height || PREVIEW_SIZE);
      }

      if (!targetUrl) {
        return;
      }

      const { renderStyledQr, renderQrToCanvas } = await loadCanvasUtils();
      if (!active) return;
      const renderStyled = async (logo) => {
        const detachedContainer = document.createElement('div');
        const instance = await renderStyledQr({
          container: detachedContainer,
          text: targetUrl,
          foregroundColor,
          backgroundColor,
          logoFileOrUrl: logo,
          logoScale,
          dotStyle,
          cornerSquareStyle,
          cornerDotStyle,
          preset,
          size: PREVIEW_SIZE,
          margin: 16,
        });
        if (!active) return false;
        if (!detachedContainer.querySelector('svg, canvas')) {
          throw new Error('Styled QR did not attach a graphic.');
        }
        container.replaceChildren(...detachedContainer.childNodes);
        styledInstanceRef.current = instance;
        renderedLogoRef.current = Boolean(logo);
        setIsFallbackMode(false);
        return true;
      };

      try {
        await renderStyled(null);
        if (!active) return;
        if (logoFileOrUrl) {
          setIsLogoOmitted(true);
          renderStyled(logoFileOrUrl)
            .then((rendered) => {
              if (active && rendered) setIsLogoOmitted(false);
            })
            .catch((err) => {
              if (active) console.warn('QR logo could not load; keeping the styled preview without it.', err);
            });
        }
      } catch (err) {
        console.warn('Styled QR preview failed, using square renderer fallback.', err);
        if (!active) return;
        try {
          const renderedCanvas = document.createElement('canvas');
          await renderQrToCanvas({
            canvas: renderedCanvas,
            text: targetUrl,
            foregroundColor,
            backgroundColor,
            logoFile: null,
            logoScale,
            logoPaddingPx: 1,
          });
          if (active) {
            canvas.width = renderedCanvas.width;
            canvas.height = renderedCanvas.height;
            canvas.getContext('2d')?.drawImage(renderedCanvas, 0, 0);
            fallbackReadyRef.current = true;
            setIsFallbackMode(true);
            setIsLogoOmitted(Boolean(logoFileOrUrl));
          }
        } catch (fallbackError) {
          console.warn('QR preview render failed.', fallbackError);
        }
      }
    }

    renderPromiseRef.current = renderPreview().catch((err) => {
      console.warn('QR preview render failed.', err);
      if (active) setIsFallbackMode(true);
    });

    return () => {
      active = false;
      styledInstanceRef.current = null;
      fallbackReadyRef.current = false;
      renderedLogoRef.current = false;
      container?.replaceChildren();
    };
  }, [
    backgroundColor,
    cornerDotStyle,
    cornerSquareStyle,
    dotStyle,
    foregroundColor,
    logoFileOrUrl,
    logoScale,
    preset,
    targetUrl,
  ]);

  return (
    <Stack spacing={1} alignItems="center">
      <Box
        sx={{
          width: PREVIEW_SIZE + 24,
          minHeight: PREVIEW_SIZE + 24,
          p: 1.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#f8faf8',
          boxShadow: (theme) => theme.custom?.softShadow ?? '0 10px 24px rgba(0, 0, 0, 0.08)',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            width: PREVIEW_SIZE,
            height: PREVIEW_SIZE,
            display: isFallbackMode ? 'none' : 'grid',
            placeItems: 'center',
            '& svg, & canvas': {
              width: `${PREVIEW_SIZE}px !important`,
              height: `${PREVIEW_SIZE}px !important`,
              display: 'block',
            },
          }}
        />
        <canvas
          ref={fallbackCanvasRef}
          width={PREVIEW_SIZE}
          height={PREVIEW_SIZE}
          style={{
            width: PREVIEW_SIZE,
            height: PREVIEW_SIZE,
            display: isFallbackMode ? 'block' : 'none',
            backgroundColor,
          }}
        />
      </Box>

      {isFallbackMode ? (
        <Chip size="small" label="Fallback square renderer active" variant="outlined" />
      ) : isLogoOmitted ? (
        <Chip size="small" label="Logo pending or unavailable; preview has no logo" variant="outlined" />
      ) : (
        <Typography variant="caption" color="text.secondary">
          Styled preview uses rounded/vector rendering with error correction level H.
        </Typography>
      )}
    </Stack>
  );
});

export default QrCanvas;
